import { useState, useEffect, useCallback } from "react";
import type { CrmTaskType } from "@ease/shared/crm-task-registry";
import { apiFetch, ApiError } from "@/lib/api-client";

const TASK_LIST_LIMIT = 100;
const TASK_LIST_MAX_AUTO_PAGES = 25;

export interface TaskSummary {
  id: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  status: string;
  personId: string | null;
  dealId: string | null;
  companyId: string | null;
  offerId: string | null;
  assignedTo: string | null;
  dueAt: string | null;
  completedAt: string | null;
  completedBy: string | null;
  source: string;
  sourceRuleId: string | null;
  template: string | null;
  notes: string | null;
  outcome: string | null;
  outcomeNotes: string | null;
  nextAction: string | null;
  followUpDate: string | null;
  contextSnapshot: Record<string, unknown>;
  scriptTemplate: string | null;
  triggerType: string | null;
  triggerEventId: string | null;
  metadata: Record<string, unknown>;
  operatorReadModel?: TaskOperatorReadModel;
  createdAt: string;
  updatedAt: string;
}

export interface TaskOperatorReadModel {
  contextSummary: string | null;
  guidance: string | null;
  operatorBrief: Array<{ label: string; value: string }>;
  completionSchema: {
    category: string;
    defaultFollowUpType: CrmTaskType;
    outcomes: Array<{
      value: string;
      label: string;
      requiresNotes: boolean;
      suggestsFollowUp: boolean;
    }>;
    fields: Array<{
      key: string;
      label: string;
      type: "select" | "text" | "date";
      required?: boolean;
      requiredOutcomes?: string[];
      options?: Array<{ value: string; label: string }>;
    }>;
  };
  automationResolution: {
    title: string;
    reason: string;
    eventType: string | null;
    eventId: string | null;
  } | null;
  automationPolicy: {
    title: string;
    summary: string;
    autoCompleteEvents: string[];
    autoCancelEvents: string[];
    conditionalEvents: string[];
  } | null;
  sla: {
    status: "closed" | "overdue" | "due_soon" | "scheduled" | "unscheduled";
    label: string;
    severity: "neutral" | "info" | "warning" | "critical";
    dueAt: string | null;
    minutesUntilDue: number | null;
    policyId: string | null;
  };
  escalation: {
    state: "candidate" | "escalated";
    level: string | null;
    reason: string;
    target: string | null;
    escalatedAt: string | null;
  } | null;
  sourceTypeLabel: string | null;
  contextCompleteness: {
    isUsable: boolean;
    hasContext: boolean;
    hasGuidance: boolean;
    hasSourceRule: boolean;
    hasMeaningfulMetadata: boolean;
    missing: string[];
  };
  labels: {
    type: string;
    canonicalType: CrmTaskType;
    source: string | null;
    priority: string | null;
    status: string | null;
  };
}

export interface TaskLifecycleEventSummary {
  id: string;
  taskId: string;
  eventType: string;
  eventSource: string;
  actorId: string | null;
  actorType: string | null;
  previousStatus: string | null;
  nextStatus: string | null;
  previousAssignee: string | null;
  nextAssignee: string | null;
  outcome: string | null;
  reason: string | null;
  notes: string | null;
  triggerEventType: string | null;
  triggerEventId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface TaskHistoryPage {
  items: TaskLifecycleEventSummary[];
  total: number;
  nextCursor: string | null;
}

export interface TaskAccountabilitySummary {
  windowDays: number;
  generatedAt: string;
  totals: {
    openTasks: number;
    overdueTasks: number;
    dueSoonTasks: number;
    completedTasks: number;
    skippedTasks: number;
    autoCancelledTasks: number;
    manuallyCancelledTasks: number;
    veryFastCompletions: number;
    completionsWithoutNotes: number;
    noAnswerWithoutNotes: number;
    followupsCreated: number;
  };
  rates: {
    overduePressurePct: number;
    completionEvidencePct: number;
    qualityIssuePct: number;
    autoResolutionPct: number;
    followUpCapturePct: number;
  };
  staff: Array<{
    staffId: string;
    openTasks: number;
    overdueTasks: number;
    dueSoonTasks: number;
    completedTasks: number;
    skippedTasks: number;
    autoCancelledTasks: number;
    manuallyCancelledTasks: number;
    veryFastCompletions: number;
    completionsWithoutNotes: number;
    noAnswerWithoutNotes: number;
    followupsCreated: number;
    completionEvidencePct: number;
    qualityIssuePct: number;
    attentionFlags: string[];
  }>;
  breakdowns: {
    byType: Array<{
      taskType: string;
      openTasks: number;
      overdueTasks: number;
      completedTasks: number;
      skippedTasks: number;
      autoCancelledTasks: number;
      veryFastCompletions: number;
      completionsWithoutNotes: number;
      noAnswerWithoutNotes: number;
      completionEvidencePct: number;
      qualityIssuePct: number;
    }>;
    byOutcome: Array<{
      outcome: string;
      completedTasks: number;
      veryFastCompletions: number;
      completionsWithoutNotes: number;
      noAnswerWithoutNotes: number;
      completionEvidencePct: number;
      qualityIssuePct: number;
    }>;
  };
  meta: {
    degraded: boolean;
    reason?: string;
  };
}

export type TaskRecurrenceFrequency = "daily" | "weekly" | "biweekly" | "monthly";
export type TaskCompletionFollowUpType = CrmTaskType;

export interface TaskRecurrenceInput {
  frequency: TaskRecurrenceFrequency;
  interval?: number;
  endsAt?: string | null;
}

export interface CompleteTaskInput {
  outcome?: string;
  outcomeNotes?: string;
  nextAction?: string;
  followUpDate?: string;
  metadata?: Record<string, unknown>;
  followUpTask?: {
    title?: string;
    description?: string;
    type?: TaskCompletionFollowUpType;
    priority?: string;
    dueAt: string;
    assignedTo?: string;
    notes?: string;
  };
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  priority?: string;
  assignedTo?: string | null;
  dueAt?: string;
  notes?: string;
  outcome?: string;
  outcomeNotes?: string;
  nextAction?: string;
  followUpDate?: string;
  contextSnapshot?: Record<string, unknown>;
  scriptTemplate?: string;
  metadata?: Record<string, unknown>;
}

interface UseTaskListParams {
  assignedTo?: string;
  personId?: string;
  dealId?: string;
  companyId?: string;
  status?: string;
  type?: string;
  source?: string;
  sourceRuleId?: string;
  triggerType?: string;
  dueBefore?: string;
  dueAfter?: string;
  limit?: number;
}

interface TaskListPageResponse {
  data: {
    items: TaskSummary[];
    total?: number;
    nextCursor?: string | null;
  };
}

export function useTaskList(params: UseTaskListParams) {
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loadedTasks: TaskSummary[] = [];
      const seenCursors = new Set<string>();
      let cursor: string | null = null;
      let remainingCursor: string | null = null;
      let reportedTotal: number | null = null;

      // Auto-page list views so operators do not silently miss tasks after the
      // API page size. The hard cap protects the UI from a runaway cursor bug.
      for (let page = 0; page < TASK_LIST_MAX_AUTO_PAGES; page += 1) {
        const sp = new URLSearchParams({ limit: String(params.limit ?? TASK_LIST_LIMIT) });
        if (params.assignedTo) sp.set("assignedTo", params.assignedTo);
        if (params.personId) sp.set("personId", params.personId);
        if (params.dealId) sp.set("dealId", params.dealId);
        if (params.companyId) sp.set("companyId", params.companyId);
        if (params.status) sp.set("status", params.status);
        if (params.type) sp.set("type", params.type);
        if (params.source) sp.set("source", params.source);
        if (params.sourceRuleId) sp.set("sourceRuleId", params.sourceRuleId);
        if (params.triggerType) sp.set("triggerType", params.triggerType);
        if (params.dueBefore) sp.set("dueBefore", params.dueBefore);
        if (params.dueAfter) sp.set("dueAfter", params.dueAfter);
        if (cursor) sp.set("cursor", cursor);

        const res = await apiFetch<TaskListPageResponse>(`/crm/tasks?${sp.toString()}`);
        loadedTasks.push(...res.data.items);
        reportedTotal = typeof res.data.total === "number" ? res.data.total : reportedTotal;
        remainingCursor = res.data.nextCursor ?? null;

        if (!remainingCursor || seenCursors.has(remainingCursor)) {
          remainingCursor = null;
          break;
        }

        seenCursors.add(remainingCursor);
        cursor = remainingCursor;
      }

      setTasks(loadedTasks);
      setTotal(reportedTotal ?? loadedTasks.length);
      setNextCursor(remainingCursor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load tasks");
    } finally {
      setIsLoading(false);
    }
  }, [
    params.assignedTo,
    params.companyId,
    params.dealId,
    params.dueAfter,
    params.dueBefore,
    params.limit,
    params.personId,
    params.source,
    params.sourceRuleId,
    params.status,
    params.triggerType,
    params.type,
  ]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  return { tasks, total, nextCursor, isLoading, error, refetch: fetchTasks };
}

export function useTaskAccountability(days = 30) {
  const [summary, setSummary] = useState<TaskAccountabilitySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccountability = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const sp = new URLSearchParams({ days: String(days) });
      const res = await apiFetch<{ data: TaskAccountabilitySummary }>(`/crm/tasks/accountability?${sp.toString()}`);
      setSummary(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load task accountability");
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAccountability(); }, [fetchAccountability]);

  return { summary, isLoading, error, refetch: fetchAccountability };
}

export async function completeTask(taskId: string, data: CompleteTaskInput = {}): Promise<void> {
  await apiFetch(`/crm/tasks/${taskId}/complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function skipTask(taskId: string, reason: string): Promise<void> {
  await apiFetch(`/crm/tasks/${taskId}/skip`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function updateTask(taskId: string, data: UpdateTaskInput): Promise<TaskSummary> {
  const res = await apiFetch<{ data: TaskSummary }>(`/crm/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return res.data;
}

export async function fetchTaskDetail(taskId: string): Promise<TaskSummary> {
  const res = await apiFetch<{ data: TaskSummary }>(`/crm/tasks/${taskId}`);
  return res.data;
}

export async function fetchTaskHistory(
  taskId: string,
  options: { limit?: number; cursor?: string | null } = {},
): Promise<TaskHistoryPage> {
  const params = new URLSearchParams();
  params.set("limit", String(options.limit ?? 25));
  if (options.cursor) params.set("cursor", options.cursor);

  const res = await apiFetch<{ data: TaskHistoryPage }>(`/crm/tasks/${taskId}/history?${params.toString()}`);
  return {
    items: res.data.items,
    total: res.data.total,
    nextCursor: res.data.nextCursor,
  };
}

export async function createTask(data: {
  title: string;
  description?: string;
  type?: CrmTaskType;
  dueAt?: string;
  assignedTo?: string;
  personId?: string;
  dealId?: string;
  companyId?: string;
  offerId?: string;
  priority?: string;
  recurrence?: TaskRecurrenceInput;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  await apiFetch("/crm/tasks", {
    method: "POST",
    body: JSON.stringify({ ...data, source: "manual" }),
  });
}
